import { colors as themeColors } from '../utils/theme';
import { Box } from '@mui/material';
import { motion } from 'framer-motion';

const SkeletonLoader = ({ count = 4, height = 80 }) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0,
      },
    },
    exit: { opacity: 0, transition: { duration: 0.2 } },
  };

  const skeletonVariants = {
    hidden: { opacity: 0, y: 12 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: 'easeOut',
      },
    },
    exit: { opacity: 0, y: -12, transition: { duration: 0.2 } },
  };

  const pulseVariants = {
    initial: { opacity: 0.5 },
    animate: {
      opacity: [0.5, 1, 0.5],
      transition: {
        duration: 2.2,
        repeat: Infinity,
        ease: 'easeInOut',
      },
    },
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        width: '100%',
      }}
    >
      {Array.from({ length: count }).map((_, i) => (
        <motion.div key={i} variants={skeletonVariants}>
          <Box
            sx={{
              borderRadius: '8px',
              overflow: 'hidden',
              border: `1px solid ${themeColors.neutral.line}`,
              backgroundColor: themeColors.neutral.surface,
              boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
              height: `${height}px`,
            }}
          >
            {/* Header bar */}
            <motion.div
              variants={pulseVariants}
              initial="initial"
              animate="animate"
              style={{
                height: 4,
                background: `linear-gradient(90deg, ${themeColors.neutral.line} 0%, #d1d5db 50%, ${themeColors.neutral.line} 100%)`,
                backgroundSize: '200% 100%',
              }}
            />

            <Box sx={{ p: 3 }}>
              {/* Avatar + Text skeleton */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                <motion.div
                  variants={pulseVariants}
                  initial="initial"
                  animate="animate"
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 6,
                    backgroundColor: themeColors.neutral.line,
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ flex: 1, width: '100%' }}>
                  <motion.div
                    variants={pulseVariants}
                    initial="initial"
                    animate="animate"
                    style={{
                      height: 14,
                      borderRadius: 4,
                      backgroundColor: themeColors.neutral.line,
                      marginBottom: 8,
                      width: '70%',
                    }}
                  />
                  <motion.div
                    variants={pulseVariants}
                    initial="initial"
                    animate="animate"
                    style={{
                      height: 12,
                      borderRadius: 4,
                      backgroundColor: '#f3f4f6',
                      width: '50%',
                    }}
                  />
                </Box>
              </Box>

              {/* Divider */}
              <Box sx={{ height: 1, backgroundColor: themeColors.neutral.line, my: 2 }} />

              {/* Badge skeletons */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <motion.div
                  variants={pulseVariants}
                  initial="initial"
                  animate="animate"
                  style={{
                    height: 24,
                    width: '45%',
                    borderRadius: 6,
                    backgroundColor: '#f3f4f6',
                  }}
                />
                <motion.div
                  variants={pulseVariants}
                  initial="initial"
                  animate="animate"
                  style={{
                    height: 24,
                    width: '40%',
                    borderRadius: 6,
                    backgroundColor: '#f3f4f6',
                  }}
                />
              </Box>
            </Box>
          </Box>
        </motion.div>
      ))}
    </motion.div>
  );
};

export default SkeletonLoader;
